import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import SidebarHeaderV2 from './SidebarHeaderV2';
import SidebarCategoryV2 from './SidebarCategoryV2';

import { useSidebar } from '../../hooks/sidebar/useSidebar';
import { useBranch } from '../../hooks/branches/useBranch';
import { persistSidebarExpanded } from '../../utils/sidebarPreference';
import { RootState } from '../../store/store';

/**
 * 📦 SidebarV2
 *
 * Sidebar dinámico:
 * ✅ ERP normal
 * ✅ POS compacto
 * ✅ Responsive
 */
interface SidebarV2Props {
  // 🔹 Mobile overlay
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;

  // 🔹 Desktop compact mode
  sidebarExpanded: boolean;
  setSidebarExpanded: (expanded: boolean) => void;

  // 🔹 POS mode
  compactMode?: boolean;
}

const SidebarV2: React.FC<SidebarV2Props> = ({
  sidebarOpen,
  setSidebarOpen,

  sidebarExpanded,
  setSidebarExpanded,

  compactMode = false,
}) => {
  // =====================================================
  // 🔹 Refs
  // =====================================================
  const sidebar = useRef<HTMLDivElement>(null);

  // =====================================================
  // 📡 Sidebar data
  // =====================================================
  const { categories, fetchSidebarData } = useSidebar();
  const { myBranch, fetchMyBranch } = useBranch();

  // Identidad del usuario autenticado: si cambia (cambio de sesión) hay que
  // recargar el sidebar, no dejar el menú del usuario anterior.
  const authEmail = useSelector((state: RootState) => state.auth.email);

  // =====================================================
  // 🚀 Inicialización / recarga por cambio de usuario
  // =====================================================
  useEffect(() => {
    if (authEmail && !categories.length) fetchSidebarData();
    // Sucursal activa del usuario: se carga una sola vez por sesión, en el
    // mismo momento que el sidebar, para que las pantallas migradas ya no
    // necesiten pedirla manualmente.
    if (authEmail && !myBranch) fetchMyBranch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authEmail, categories.length, myBranch]);

  // =====================================================
  // 💾 Persistencia
  // =====================================================
  useEffect(() => {
    // Solo persiste desde escritorio; en pantallas < 1280 el sidebar arranca
    // siempre expandido y no debe sobrescribir la preferencia de escritorio.
    persistSidebarExpanded(sidebarExpanded);

    document.body.classList.toggle('sidebar-expanded', sidebarExpanded);
  }, [sidebarExpanded]);

  // =====================================================
  // 🎨 Width dinámico
  // =====================================================
  const sidebarWidth = sidebarExpanded ? 'w-72.5' : 'w-20';

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <aside
      id="sidebar"
      ref={sidebar}
      className={`
        absolute
        left-0
        top-0
        z-9999

        flex
        h-screen
        flex-col

        overflow-y-auto

        bg-black
        transition-all
        duration-300
        ease-linear

        dark:bg-boxdark

        lg:static
        lg:translate-x-0

        ${sidebarWidth}

        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
    >
      {/* ===================================================== */}
      {/* 🔹 Header */}
      {/* ===================================================== */}
      <SidebarHeaderV2
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        compactMode={compactMode}
        sidebarExpanded={sidebarExpanded}
      />

      {/* ===================================================== */}
      {/* 🔹 Categorías */}
      {/* ===================================================== */}
      <SidebarCategoryV2
        categories={categories}
        sidebarExpanded={sidebarExpanded}
        setSidebarExpanded={setSidebarExpanded}
        compactMode={compactMode}
      />
    </aside>
  );
};

export default SidebarV2;
